/**
 * Utilidades de fecha "sin hora".
 *
 * Todo el dominio (vencimientos, plazos, cronogramas) razona en días de
 * calendario, no en instantes. El CRM original mezclaba `new Date("2026-04-15")`
 * —que JavaScript interpreta como medianoche UTC— con métodos locales como
 * `getDay()`, lo que en Chile (UTC-3/-4) corre las fechas un día en algunos
 * casos. Aquí todo se normaliza a mediodía UTC: así ningún cambio de horario
 * de verano ni offset puede empujar una fecha al día anterior o siguiente.
 */
const MS_POR_DIA = 86_400_000;
const RE_FECHA_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Convierte cualquier entrada razonable a un `Date` anclado a mediodía UTC.
 * Devuelve `null` si no se puede interpretar.
 */
export function aFecha(valor) {
    if (valor == null)
        return null;
    if (valor instanceof Date) {
        if (Number.isNaN(valor.getTime()))
            return null;
        return new Date(Date.UTC(valor.getUTCFullYear(), valor.getUTCMonth(), valor.getUTCDate(), 12));
    }
    const texto = valor.trim();
    if (!texto)
        return null;
    const soloFecha = RE_FECHA_ISO.exec(texto);
    if (soloFecha) {
        const [, anio, mes, dia] = soloFecha;
        return new Date(Date.UTC(Number(anio), Number(mes) - 1, Number(dia), 12));
    }
    const parseada = new Date(texto);
    if (Number.isNaN(parseada.getTime()))
        return null;
    return new Date(Date.UTC(parseada.getUTCFullYear(), parseada.getUTCMonth(), parseada.getUTCDate(), 12));
}
/** Igual que `aFecha` pero lanza en vez de devolver `null`. */
export function aFechaObligatoria(valor) {
    const fecha = aFecha(valor);
    if (!fecha)
        throw new Error(`Fecha inválida: ${String(valor)}`);
    return fecha;
}
/** Serializa a `YYYY-MM-DD`. */
export function aISO(valor) {
    const fecha = aFecha(valor);
    if (!fecha)
        return null;
    const mes = String(fecha.getUTCMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getUTCDate()).padStart(2, '0');
    return `${fecha.getUTCFullYear()}-${mes}-${dia}`;
}
/** Hoy, anclado a mediodía UTC. Parámetro inyectable para que los tests sean deterministas. */
export function hoy(referencia = new Date()) {
    return aFechaObligatoria(referencia);
}
export function sumarDias(valor, dias) {
    const fecha = aFechaObligatoria(valor);
    return new Date(fecha.getTime() + Math.round(dias) * MS_POR_DIA);
}
/**
 * Suma meses respetando el fin de mes: 31 de enero + 1 mes = 28 de febrero,
 * no el 3 de marzo (que es lo que hace `setMonth` a secas, como en el CRM original).
 */
export function sumarMeses(valor, meses) {
    const fecha = aFechaObligatoria(valor);
    const anio = fecha.getUTCFullYear();
    const mes = fecha.getUTCMonth();
    const dia = fecha.getUTCDate();
    const mesDestino = mes + Math.round(meses);
    const ultimoDiaDestino = new Date(Date.UTC(anio, mesDestino + 1, 0, 12)).getUTCDate();
    return new Date(Date.UTC(anio, mesDestino, Math.min(dia, ultimoDiaDestino), 12));
}
/** Días calendario entre dos fechas (`hasta - desde`). Positivo si `hasta` es posterior. */
export function diasEntre(desde, hasta) {
    const a = aFechaObligatoria(desde);
    const b = aFechaObligatoria(hasta);
    return Math.round((b.getTime() - a.getTime()) / MS_POR_DIA);
}
/** Meses (fraccionarios) transcurridos desde `fecha` hasta `referencia`. Nunca negativo. */
export function mesesTranscurridos(fecha, referencia = new Date()) {
    const desde = aFecha(fecha);
    if (!desde)
        return Number.POSITIVE_INFINITY;
    return Math.max(0, diasEntre(desde, hoy(referencia)) / 30.4375);
}
/** Clave `YYYY-MM`, útil para agrupar series mensuales. */
export function claveMes(valor) {
    const iso = aISO(valor);
    return iso ? iso.slice(0, 7) : null;
}
/** Primer día del mes de `valor`. */
export function inicioDeMes(valor) {
    const fecha = aFechaObligatoria(valor);
    return new Date(Date.UTC(fecha.getUTCFullYear(), fecha.getUTCMonth(), 1, 12));
}
/** Último día del mes de `valor`. */
export function finDeMes(valor) {
    const fecha = aFechaObligatoria(valor);
    return new Date(Date.UTC(fecha.getUTCFullYear(), fecha.getUTCMonth() + 1, 0, 12));
}
/**
 * Lista de claves `YYYY-MM` consecutivas entre dos fechas, ambas inclusive.
 * Se usa para que las series de gráficos no salteen meses sin datos.
 */
export function rangoDeMeses(desde, hasta) {
    const inicio = inicioDeMes(desde);
    const fin = inicioDeMes(hasta);
    const claves = [];
    let cursor = inicio;
    // Tope de seguridad: 50 años. Evita un bucle infinito si llegan fechas absurdas.
    for (let i = 0; cursor.getTime() <= fin.getTime() && i < 600; i++) {
        claves.push(claveMes(cursor));
        cursor = sumarMeses(cursor, 1);
    }
    return claves;
}
/** `true` si ambas fechas caen el mismo día. */
export function mismoDia(a, b) {
    const isoA = aISO(a);
    const isoB = aISO(b);
    return isoA !== null && isoA === isoB;
}
/** Día de la semana con lunes = 0, domingo = 6 (como la grilla del calendario original). */
export function diaSemanaLunesPrimero(valor) {
    const fecha = aFechaObligatoria(valor);
    return (fecha.getUTCDay() + 6) % 7;
}
export const NOMBRES_MES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
export const NOMBRES_DIA_CORTOS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
/** Etiqueta legible de un mes a partir de su clave `YYYY-MM`. */
export function etiquetaMes(clave) {
    const [anio, mes] = clave.split('-');
    const indice = Number(mes) - 1;
    const nombre = NOMBRES_MES[indice];
    return nombre ? `${nombre} ${anio}` : clave;
}
//# sourceMappingURL=fecha.js.map